import { hasArrayElements } from "@/apiUtils/commonUtils";
import { lensConfigurationSteps } from "@/apiUtils/constants";
import { ChoiceType, TranslationData } from "@/common/lib/types";
import RenderPrice from "@/components/productPrice/RenderPrice";
import { cn } from "@/lib/tailwind";
import { getLangFromLocale, handleGetDiscount } from "@/utils/common";
import { translate } from "@/utils/lensConfigurator";
import { useMemo } from "react";
import { useConfiguratorContext } from "../context/Provider";
import SummaryView from "./SummaryView";
import TotalSection from "./TotalSection";

interface ISummaryProps {
  locale: string;
  className?: string;
  currencyCode: string;
  isPurchased?: boolean;
  translations: TranslationData;
}

const SummaryReview = ({ locale, className, currencyCode, translations }: ISummaryProps) => {
  const language = getLangFromLocale(locale);
  const { currentStep, productInfo, summary } = useConfiguratorContext();

  const total = useMemo(() => {
    return summary.reduce(
      (acc, item) =>
        acc + (item.price === "Included" ? 0 : parseFloat(item?.offerPrice || item?.price || "0")),
      0,
    );
  }, [summary]);

  const isPrescriptionStep = useMemo(
    () =>
      currentStep === lensConfigurationSteps.PRESCRIPTION ||
      currentStep === lensConfigurationSteps.UPLOAD_PRESCRIPTION,
    [currentStep],
  );

  const modelListPrice = productInfo?.price?.listPrice ?? 0;

  return (
    <section className={cn("mt-9 block bg-primary-4 px-5 py-6 lg:hidden", className)}>
      <h3 className="mb-[1.875rem] text-base font-normal">
        {translations?.lensConfigurator?.lens_config_summary_label}
      </h3>
      {/* Options Section */}
      {!isPrescriptionStep && hasArrayElements(summary) && (
        <section>
          {summary.map((item, index) => {
            const choice = ((item.choices ?? []).at(0) ?? {}) as ChoiceType;
            const modelSalePrice = Number(item.price);
            // Same as PDP: costPrice/list vs sale via handleGetDiscount
            const discountPercentage =
              item.key === "model-price" &&
              modelListPrice >= 1 &&
              !Number.isNaN(modelSalePrice) &&
              modelSalePrice > 0
                ? handleGetDiscount(modelListPrice, modelSalePrice)
                : "";
            const modelHasDiscount = Boolean(discountPercentage);

            let priceNode: React.ReactNode = null;
            if (item.offerPrice) {
              priceNode = (
                <p>
                  <del>
                    <RenderPrice price={item.price} currencyCode={currencyCode} lang={language} />
                  </del>
                  <RenderPrice price={item.offerPrice} currencyCode={currencyCode} lang={language} />{" "}
                  {/* TODO(QA): When Qatar (qa) is enabled, gate offerPercent with NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES
                      via getPercentHideCountries()/displayPercentage (same as Magrabi LCP review/summary). */}
                  {item.offerPercent}
                </p>
              );
            } else if (modelHasDiscount) {
              priceNode = (
                <div className="flex flex-col items-end">
                  <del className="text-gray-500">
                    <RenderPrice price={modelListPrice} currencyCode={currencyCode} lang={language} />
                  </del>
                  <span className="text-red-500">
                    <RenderPrice price={item.price} currencyCode={currencyCode} lang={language} />
                  </span>
                  {/* TODO(QA): When Qatar (qa) is enabled, gate this % with NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES
                      via getPercentHideCountries()/displayPercentage (same as Magrabi LCP review/summary). */}
                  <span className="text-sm font-semibold">-{discountPercentage}%</span>
                </div>
              );
            } else if (item.price === "Included") {
              priceNode = "Included";
            } else {
              priceNode = (
                <RenderPrice price={item.price} currencyCode={currencyCode} lang={language as string} />
              );
            }

            return (
              <section key={index} className="my-4">
                <SummaryView
                  name={translate(choice?.displayValue, language)}
                  title={translations?.lensConfigurator?.[item?.option]}
                  price={priceNode}
                />
                <hr className={cn("-sm:mx-12 -mx-5 mt-4 bg-tertiary-8")} />
              </section>
            );
          })}
        </section>
      )}
      <TotalSection
        total={{
          label: translations?.lensConfigurator?.lens_config_total_label || "",
          amount: <RenderPrice price={total} currencyCode={currencyCode} lang={language} />,
        }}
      />
    </section>
  );
};

export default SummaryReview;
